import React, { useEffect, useRef, useState } from "react";
import {
  Collapse,
  Form,
  Button,
  message,
  Typography,
  Row,
  Col,
} from "antd";
import ConsultantForm from "./CommonComponents/ConsultantForm";
import PatientProfile from "./CommonComponents/PatientProfile";
import ClinicalImpression from "./CommonComponents/ClinicalImpression";
import HistoryIllness from "./CommonComponents/HistoryIllness";
import FunctionalStatus from "./CommonComponents/FunctionalStatus";
import ClinicalProgress from "./CommonComponents/ClinicalProgress/ClinicalProgress";
import Medication from "./CommonComponents/Medication";
import Nebulization from "./CommonComponents/Nebulization";
import Vitals from "./CommonComponents/Vitals";
import DischargeFunctionalStatus from "./DischargeFunctionalStatus";
import Comments from "./CommonComponents/Comments";
import btnStyles from "../../../styles/Custom/Button.module.css";
import dayjs from "dayjs";
import "./ReviewForms.css";
import "./FormsIp.css";
import {
  getPatientDetailsApi,
  getReferralFormApi,
  printFormApi,
  submitReferralFormApi,
  updateReferralFormApi,
} from "../../../services/forms.services";
import { CloseOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import {
  knownAllergiesOptions,
  pastMedicalOptions,
} from "../../IpEMR/Options/historyOptions";
import {
  getLabelTagsWithFormDescription,
  getPastMedicalHistoryFormSummary,
  printFormSummary,
} from "./formsKeys";
import useQuery from "../../../hooks/useQuery";
import NoDataPage from "../../NoDataPages/NoDataPage";
import assessmentIcon from "../../../assets/images/nurseIcons/assessment.svg";
import FormContentLoader from "./FormContentLoader";
import ScaledHtmlPreview from "../../ui/ScaledHtmlPreview";

const ReferralForm = ({ patientData, role }) => {
  // console.log(patientData);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const commonStyle = {
    // background: "#fff",
    boxShadow:
      "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
  };

  const [pressureSorePoints, setPressureSorePoints] = useState([]);
  const [referralFormData, setReferralFormData] = useState(null);
  const [editable, setEditable] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [details, setDetails] = useState(null);
  const [displayForm, setDisplayForm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshPage = () => setRefresh((prev) => !prev);
  const isActive = useQuery()?.status === "current";
  const isReceptionist = role === "receptionist";
  const canEditForm = !isReceptionist && editable;

  useEffect(() => {
    if (!patientData?.admission?.id || !patientData?.doctor_intial_assessment) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setDisplayForm(null);

    const loadForm = async () => {
      try {
        const [formRes, detailsRes] = await Promise.all([
          getReferralFormApi(patientData.admission.id),
          getPatientDetailsApi(patientData.admission.id),
        ]);
        if (cancelled) return;

        const formData = formRes?.data;
        setReferralFormData(formData);
        setDetails(detailsRes?.data);

        const formId = formData?.data?.id;
        if (isReceptionist) {
          setEditable(false);
        } else {
          setEditable(formId ? false : true);
        }

        if (formId) {
          const previewRes = await printFormApi(
            `form_type=referral&form_id=${formId}`
          );
          if (!cancelled) {
            setDisplayForm(previewRes?.data?.data ?? null);
          }
        } else if (!cancelled) {
          setDisplayForm(null);
        }
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadForm();
    return () => {
      cancelled = true;
    };
  }, [patientData?.admission?.id, patientData?.doctor_intial_assessment, refresh, isReceptionist]);

  const setInitialFormValues = () => {
    form.setFieldsValue({
      ...referralFormData?.data?.data,
      clinical_progress: {
        ...referralFormData?.data?.data?.clinical_progress,
        date: referralFormData?.data?.data?.clinical_progress?.date
          ? dayjs(referralFormData?.data?.data?.clinical_progress?.date) // Keep as dayjs object
          : null,
      },
    });
    if (referralFormData?.data?.data?.pressure_sore) {
      setPressureSorePoints(referralFormData?.data?.data?.pressure_sore);
    }
  };

  useEffect(() => {
    setInitialFormValues();
  }, [referralFormData?.data?.data]);

  const onFinish = (values) => {
    const formData = {
      patient: details?.data?.patient,
      diagnosis: details?.data?.diagnosis,
      medications: details?.data?.medications,
      functional_status: details?.data?.functional_status,
      vitals: details?.data?.vitals,
      nebulization: details?.data?.nebulization,
      past_medical_history: details?.data?.present_illness
        ? getPastMedicalHistoryFormSummary(
            pastMedicalOptions,
            details?.data?.present_illness
          )
        : "",
      known_allergies: details?.data?.present_illness
        ? getLabelTagsWithFormDescription(
            knownAllergiesOptions,
            details?.data?.present_illness
          )
        : "",
      past_surgical_history: details?.data?.present_illness?.surgical_history
        ? details?.data?.present_illness?.surgical_history
        : null,
      past_surgical_history_details: details?.data?.present_illness
        ?.surgical_history_details
        ? details?.data?.present_illness?.surgical_history_details
        : null,
      ...values,

      clinical_progress: {
        ...values?.clinical_progress,
        date: values?.clinical_progress?.date
          ? dayjs(values.clinical_progress.date).format("YYYY-MM-DD")
          : null, // Handle cases where the date is not provided
      },
      pressure_sore: pressureSorePoints,
    };
    if (referralFormData?.data?.id) {
      updateReferralFormApi(patientData?.admission?.id, { data: formData })
        .then((res) => {
          message.success(res?.data?.message);
          setEditable(false);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    } else {
      submitReferralFormApi({
        ip_admission: patientData?.admission?.id,
        data: formData,
      })
        .then((res) => {
          message.success(res?.data?.message);
          setEditable(false);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    }

    // console.log(formData, "fo");
  };

  return (
    <>
      {!patientData?.doctor_intial_assessment ? (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title={"Initial Assessment Not Yet Submitted "}
              subtitle={"Kindly, submit initial assessment first"}
              image={assessmentIcon}
            />
          </Col>
        </Row>
      ) : (
        <FormContentLoader loading={isLoading} tip="Loading referral form...">
        <div style={{ position: "relative" }}>
          <div className="forms-ip-header">
            <Typography.Title level={5} className="forms-ip-header-title">
              Referral Form
            </Typography.Title>
            {referralFormData?.data?.id && (
              <div className="forms-ip-header-actions">
                {isActive && !["receptionist"]?.includes(role) && (
                  <EditIcon onClick={() => setEditable(true)} />
                )}
                <Button
                  type="primary"
                  onClick={() =>
                    printFormSummary("referral", referralFormData?.data?.id)
                  }
                >
                  Print Summary
                </Button>
                {canEditForm && (
                  <CloseOutlined
                    onClick={() => {
                      setEditable(false);
                      form.resetFields();
                      refreshPage();
                    }}
                  />
                )}
              </div>
            )}
          </div>
          {canEditForm ? (
            <Form
              form={form}
              ref={formRef}
              layout="vertical"
              onFinish={onFinish}
              className="forms-ip-shell"
              initialValues={{
                ...referralFormData?.data?.data,
                clinical_progress: {
                  ...referralFormData?.data?.data?.clinical_progress,
                  date: referralFormData?.data?.data?.clinical_progress?.date
                    ? dayjs(
                        referralFormData?.data?.data?.clinical_progress?.date
                      ) // Keep as dayjs object
                    : null,
                },
              }}
            >
              <Collapse
                expandIconPosition="end"
                className="custom-collapse referral-collapse"
                defaultActiveKey={[
                  "1",
                  "2",
                  "3",
                  "4",
                  "5",
                  "6",
                  "7",
                  "8",
                  "9",
                  "10",
                  "11",
                ]}
              >
                <Collapse.Panel
                  header="Patient Profile"
                  style={commonStyle}
                  key="1"
                >
                  <PatientProfile data={patientData?.admission} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Consultants"
                  style={commonStyle}
                  key="2"
                >
                  <ConsultantForm form={form} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Impression/ Diagnosis"
                  style={commonStyle}
                  key="3"
                >
                  <ClinicalImpression
                    form={form}
                    details={details?.data?.diagnosis}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="History of Present Illness"
                  style={commonStyle}
                  key="4"
                >
                  <HistoryIllness
                    form={form}
                    patientData={patientData?.admission}
                    data={details?.data?.present_illness}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Functional Status at the time of Admission"
                  style={commonStyle}
                  key="5"
                >
                  <FunctionalStatus
                    form={form}
                    details={details?.data?.functional_status}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Progress"
                  style={commonStyle}
                  key="6"
                >
                  <ClinicalProgress
                    form={form}
                    setPressureSorePoints={setPressureSorePoints}
                    pressureSorePoints={pressureSorePoints}
                  />
                </Collapse.Panel>
                <Collapse.Panel header="Medication" style={commonStyle} key="7">
                  <Medication form={form} details={details} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Nebulization"
                  style={commonStyle}
                  key="8"
                >
                  <Nebulization form={form} details={details} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Functional Status at Discharge / Referral"
                  style={commonStyle}
                  key="9"
                >
                  <DischargeFunctionalStatus form={form} />
                </Collapse.Panel>
                <Collapse.Panel header="Vitals" style={commonStyle} key="10">
                  <Vitals form={form} details={details} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Comments"
                  style={{ ...commonStyle, marginBottom: "40px" }}
                  key="11"
                >
                  <Comments form={form} />
                </Collapse.Panel>
              </Collapse>
              <div
                className="ip-fixed-form-footer"
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,

                  width: "100%",
                  background: "#fff",
                  paddingTop: "10px",
                  paddingRight: "16px",
                  boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
                  textAlign: "right",
                  zIndex: 1000, // Ensure it stays above other elements
                }}
              >
                {isActive && !["receptionist"]?.includes(role) && (
                  <Form.Item>
                    <Button
                      htmlType="submit"
                      type="primary"
                      className={btnStyles.lgBtn}
                    >
                      Submit
                    </Button>
                  </Form.Item>
                )}
              </div>
            </Form>
          ) : isReceptionist && !referralFormData?.data?.id ? (
            <NoDataPage
              title="Referral Form Not Available"
              subtitle="Referral form has not been submitted yet"
              image={assessmentIcon}
            />
          ) : displayForm ? (
            <ScaledHtmlPreview
              html={displayForm}
              designWidth={900}
              className="ip-form-html-preview"
            />
          ) : (
            <NoDataPage
              title="Referral Form Not Available"
              subtitle="Referral form has not been submitted yet"
              image={assessmentIcon}
            />
          )}
        </div>
        </FormContentLoader>
      )}
    </>
  );
};

export default ReferralForm;
